import { Card } from 'components/Card';
import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { format, max, subDays } from 'date-fns';
import {
  EngageScheduledVisitStatus,
  ScheduledVisitCommunicationSchedule,
  ScheduledVisitCommunicationScheduleTemplateType,
  ScheduledVisitMessageTemplate,
} from 'types/DTOs/BlockManagerDTOs';
import { CardRow } from 'components/CardRow';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { filterArrayEmpty } from 'lib/riskbase';
import { GapNone } from 'components/GapNone';
import { GapSmall } from 'components/GapSmall';
import { Pressable } from 'components/Pressable';

type EngageScheduledVisitTimelineProps = {
  templates: ScheduledVisitMessageTemplate[];
  communicationSchedule: ScheduledVisitCommunicationSchedule[];
  scheduledVisitStatus?: EngageScheduledVisitStatus;
  earliestSelectedDate?: Date | null;
  publishedDate?: Date | null;
  cancelledDate?: Date | null;
  loading: boolean;
  canSendNow: boolean;
  onSendNow: (
    templateType: ScheduledVisitCommunicationScheduleTemplateType,
  ) => void;
};

const CANCELLATION_TEMPLATE_ID =
  'door_check_scheduled_visit_cancellation_email';

const getTemplateType = (
  templateName: ScheduledVisitMessageTemplate['name'],
): ScheduledVisitCommunicationScheduleTemplateType => {
  switch (templateName) {
    case 'Chaser':
      return ScheduledVisitCommunicationScheduleTemplateType.CHASER;
    case 'Reminder':
      return ScheduledVisitCommunicationScheduleTemplateType.REMINDER;
    case 'Cancellation':
      return ScheduledVisitCommunicationScheduleTemplateType.CANCELLATION;
    case 'Request':
    default:
      return ScheduledVisitCommunicationScheduleTemplateType.REQUEST;
  }
};

export const EngageScheduledVisitTimeline: React.FC<
  EngageScheduledVisitTimelineProps
> = ({
  templates,
  communicationSchedule,
  scheduledVisitStatus,
  earliestSelectedDate,
  publishedDate,
  cancelledDate,
  loading,
  canSendNow,
  onSendNow,
}) => {
  const [isOpen, setIsOpen] = React.useState<Record<string, boolean>>({});
  const isCancelled =
    scheduledVisitStatus === EngageScheduledVisitStatus.CANCELLED;
  const isPublished =
    scheduledVisitStatus === EngageScheduledVisitStatus.PUBLISHED;
  const hasSentNonCancellationTemplate = React.useMemo(
    () =>
      communicationSchedule.some(
        (item) =>
          item.templateType !==
            ScheduledVisitCommunicationScheduleTemplateType.CANCELLATION &&
          !!item.sentDate,
      ),
    [communicationSchedule],
  );
  const requestCommunication = communicationSchedule.find(
    (item) =>
      item.templateType ===
      ScheduledVisitCommunicationScheduleTemplateType.REQUEST,
  );
  // For published Send Now logic
  const requestHasSentOrFailed =
    !!requestCommunication?.sentDate || !!requestCommunication?.failedDate;

  // Only include the cancellation template when the visit is cancelled.
  // When cancelled, keep all templates visible.
  const timelineTemplates = React.useMemo(
    () =>
      templates.filter((template) => {
        const isCancellationTemplate = template.id === CANCELLATION_TEMPLATE_ID;

        if (!isCancellationTemplate) {
          return true;
        }

        return isCancelled && hasSentNonCancellationTemplate;
      }),
    [templates, isCancelled, hasSentNonCancellationTemplate],
  );

  return (
    <GapNone>
      <Card label="Message Timeline" labelSize="large" />
      <GapSmall>
        {loading && (
          <Card>
            <CardRow contentStyle={{ paddingTop: 0, paddingBottom: 0 }}>
              <ActivityIndicator size={16} />
            </CardRow>
          </Card>
        )}
        {!loading &&
          timelineTemplates.map((template) => {
            const isCancellationTemplate =
              template.id === CANCELLATION_TEMPLATE_ID;
            const templateType = getTemplateType(template.name);
            // The send date is the latest of the following:
            // - x days before earliestSelectedDate or
            // - if published, the published date, otherwise the current date
            const sendDate = isCancellationTemplate
              ? null
              : earliestSelectedDate &&
                max(
                  [
                    subDays(earliestSelectedDate, template.sendDays),
                    publishedDate || new Date(),
                  ].filter(filterArrayEmpty),
                );

            const communication = communicationSchedule.find(
              (item) => item.templateType === templateType,
            );

            const displayDate =
              communication?.failedDate || communication?.sentDate;
            const isFailed = !!communication?.failedDate;
            const isPreviewOpen = isOpen[template.name] ?? false;
            const showCancelledStatus =
              !displayDate && isCancelled && !isCancellationTemplate;
            // No Send Now Button for these
            const hasSentOrFailed =
              !!communication?.sentDate || !!communication?.failedDate;
            // Show Send Now Button for both of these (if not sent/ failed already)
            const isReminderOrChaserTemplate =
              templateType ===
                ScheduledVisitCommunicationScheduleTemplateType.REMINDER ||
              templateType ===
                ScheduledVisitCommunicationScheduleTemplateType.CHASER;
            const hasPairedReminderOrChaserSendNowRequested =
              isReminderOrChaserTemplate &&
              communicationSchedule.some(
                (item) =>
                  (item.templateType ===
                    ScheduledVisitCommunicationScheduleTemplateType.REMINDER ||
                    item.templateType ===
                      ScheduledVisitCommunicationScheduleTemplateType.CHASER) &&
                  !!item.sendNowRequestedDate,
              );
            const hasSendNowRequested =
              !!communication?.sendNowRequestedDate ||
              hasPairedReminderOrChaserSendNowRequested;
            const canSendPublishedTemplateNow =
              isPublished &&
              ((!requestHasSentOrFailed &&
                templateType ===
                  ScheduledVisitCommunicationScheduleTemplateType.REQUEST) ||
                (requestHasSentOrFailed && isReminderOrChaserTemplate));
            const canSendTemplateNow =
              canSendNow &&
              !hasSentOrFailed &&
              !hasSendNowRequested &&
              (canSendPublishedTemplateNow ||
                (isCancelled && isCancellationTemplate));

            return (
              <Card key={template.id}>
                <CardRow
                  icon={ICONS.ChevronRightLight}
                  iconSize={16}
                  iconStyle={{
                    transform: [{ rotate: isPreviewOpen ? '90deg' : '0deg' }],
                  }}
                  onPress={() => {
                    setIsOpen((prev) => ({
                      ...prev,
                      [template.name]: !(prev[template.name] ?? false),
                    }));
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: theme.paddingHorizontalHalf / 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Txt
                      size="normal"
                      weight="medium"
                      style={{
                        flexShrink: 1,
                      }}
                    >
                      {template.name}
                    </Txt>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.paddingHorizontalHalf,
                        flexShrink: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* If we have a sent date, show it, if not show the send date */}
                      {displayDate ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: theme.paddingHorizontalHalf,
                          }}
                        >
                          <Txt
                            size="small"
                            weight="medium"
                            style={{ color: theme.colors.fieldValueText }}
                          >
                            {`was ${isFailed ? 'failed' : 'sent'} on ${format(displayDate, UI_DATE_FORMAT_LONG)}`}
                          </Txt>
                          <Icon
                            source={
                              isFailed
                                ? ICONS.CircleXmarkLight
                                : ICONS.CircleCheckLight
                            }
                            color={
                              isFailed ? theme.colors.red : theme.colors.green
                            }
                            size={20}
                          />
                        </View>
                      ) : showCancelledStatus ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: theme.paddingHorizontalHalf,
                          }}
                        >
                          <Txt
                            size="small"
                            weight="medium"
                            style={{ color: theme.colors.fieldValueText }}
                          >
                            {cancelledDate
                              ? `was cancelled on ${format(cancelledDate, UI_DATE_FORMAT_LONG)}`
                              : 'Cancelled'}
                          </Txt>
                          <Icon
                            source={ICONS.CircleXmarkLight}
                            color={theme.colors.red}
                            size={20}
                          />
                        </View>
                      ) : (
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: theme.paddingHorizontalHalf,
                          }}
                        >
                          <Txt
                            size="small"
                            weight="medium"
                            style={{ color: theme.colors.fieldValueText }}
                          >
                            {sendDate
                              ? `will be sent on ${format(sendDate, UI_DATE_FORMAT_LONG)}`
                              : 'Pending'}
                          </Txt>
                          <Icon
                            source={ICONS.ClockLight}
                            color={theme.colors.fieldValueText}
                            size={20}
                          />
                        </View>
                      )}
                      {canSendTemplateNow && (
                        <>
                          <Txt
                            size="small"
                            weight="medium"
                            style={{ color: theme.colors.fieldValueText }}
                          >
                            or
                          </Txt>
                          <Pressable
                            onPress={(event) => {
                              event.stopPropagation();
                              onSendNow(templateType);
                            }}
                            accessibilityLabel={`Send ${template.name} now`}
                          >
                            <Txt
                              size="small"
                              weight="medium"
                              style={{ color: theme.colors.blue }}
                            >
                              send now
                            </Txt>
                          </Pressable>
                        </>
                      )}
                    </View>
                  </View>
                  <Txt style={{ marginTop: 2 }} size="xsmall">
                    {template.description}
                  </Txt>
                </CardRow>
                {isPreviewOpen && (
                  <CardRow>
                    <Txt weight="medium" size="small">
                      Subject: {template.subject}
                    </Txt>
                    <Txt size="small">{template.body}</Txt>
                  </CardRow>
                )}
              </Card>
            );
          })}
      </GapSmall>
    </GapNone>
  );
};
